import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LoaderCircle, Download } from 'lucide-react';
import { renderLegalMarkdown } from './renderLegalMarkdown';

// Serves a legal document from /public/legal so the agreements we ask people
// to accept are actually readable. The signup fine print used to point at
// medilink.vip/baa, which 404s — a customer could accept a BAA they had no
// way to read. Documents are versioned by filename so an acceptance record
// naming version 2026-07-24 can always be resolved to the exact text.
export const LEGAL_DOCS = {
  baa: {
    file: '/legal/baa-2026-10-23.md',
    version: '2026-10-23',
    title: 'Business Associate Agreement',
    subtitle: 'HIPAA · 45 C.F.R. §§ 164.502(e), 164.504(e)',
  },
};


export default function LegalDocPage({ doc = 'baa' }) {
  const meta = LEGAL_DOCS[doc];
  const [html, setHtml] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    fetch(meta.file)
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.text(); })
      .then((t) => { if (alive) setHtml(renderLegalMarkdown(t)); })
      .catch(() => { if (alive) setError('This document could not be loaded. Please contact support@medilink.vip.'); });
    return () => { alive = false; };
  }, [meta.file]);

  return (
    <div className="min-h-screen bg-primary p-4 md:p-8">
      <div className="max-w-[820px] mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <Link to="/login" className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors">
            &larr; Back to sign in
          </Link>
          <a
            href={meta.file}
            download
            className="inline-flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-[10px] bg-elevated border border-theme text-primary-c hover:border-strong transition-colors"
          >
            <Download size={13} /> Download
          </a>
        </div>

        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-10 shadow-lg">
          <p className="text-[11px] font-semibold tracking-wide uppercase text-muted-c mb-1">
            {meta.subtitle}
          </p>
          <h1 className="text-[22px] font-bold text-primary-c mb-1">{meta.title}</h1>
          <p className="text-[12px] text-muted-c mb-7">Version {meta.version}</p>

          {error && (
            <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">{error}</div>
          )}
          {!error && html === null && (
            <div className="flex items-center gap-2 text-[13px] text-muted-c">
              <LoaderCircle size={15} className="animate-spin" /> Loading…
            </div>
          )}
          {html !== null && (
            <div className="legal-doc text-[13px] text-secondary-c" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    </div>
  );
}
